import { DateTime } from '@ksyos/date-time';
import { Box, DatePicker, FormGroup, IconButton, TextInput } from '@ksyos/design-system';
import type { ValidationErrors } from '@frontend/utils';
import { EpisodeRecord } from '@frontend/components/pages/customControls/utils/v1/episodeJournals/episodeJournals';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils';

type EpisodesEntryAddEditProps = {
    episodesEntry: EpisodeRecord;
    episodesEntryId: string;
    errorKey: number;
    errors?: ValidationErrors;
    onChange: (episode: EpisodeRecord, episodeKey: string, validationKeys: string[]) => void;
    onDelete: (value: string, episodeId: string) => void;
};

export function EpisodesEntryAddEdit(props: Readonly<EpisodesEntryAddEditProps>) {
    const { errorKey, errors, episodesEntry, episodesEntryId, onChange, onDelete } = props;
    const translate = getSelectedLanguage();
    const validationKeys: string[] = [];

    if (!episodesEntryId) {
        return null;
    }

    return (
        <Box
            display="grid"
            gap={[1, 2]}
            gridTemplateColumns="repeat(3, minmax(220px, 1fr)) max-content max-content max-content"
        >
            <FormGroup
                isRequired={true}
                label={translate.episodes.displayName}
                layout="vertical"
            >
                <TextInput
                    error={getValidationErrorTranslation(
                        errors?.get('episodesAndJournals.episodes.' + errorKey + '.valueDisplayName')
                    )}
                    onChange={async (e) => {
                        handleChangeEpisodesEntryValue(e.target.value ?? null, 'valueDisplayName');
                    }}
                    value={episodesEntry.valueDisplayName ? episodesEntry.valueDisplayName : ''}
                />
            </FormGroup>
            <FormGroup
                isRequired={true}
                label={translate.episodes.description}
                layout="vertical"
            >
                <TextInput
                    error={getValidationErrorTranslation(
                        errors?.get('episodesAndJournals.episodes.' + errorKey + '.comment')
                    )}
                    onChange={async (e) => {
                        handleChangeEpisodesEntryValue(e.target.value ?? null, 'comment');
                    }}
                    value={episodesEntry.comment ? episodesEntry.comment : ''}
                />
            </FormGroup>
            <FormGroup
                isRequired={true}
                label={translate.episodes.icpc}
                layout="vertical"
            >
                <TextInput
                    error={getValidationErrorTranslation(
                        errors?.get('episodesAndJournals.episodes.' + errorKey + '.statusCode')
                    )}
                    onChange={async (e) => {
                        handleChangeEpisodesEntryValue(e.target.value ?? null, 'statusCode');
                    }}
                    value={episodesEntry.statusCode ? episodesEntry.statusCode : ''}
                />
            </FormGroup>
            <FormGroup
                isRequired={true}
                label={translate.common.startDate}
                layout="vertical"
            >
                <DatePicker
                    error={getValidationErrorTranslation(
                        errors?.get('episodesAndJournals.episodes.' + errorKey + '.startDate')
                    )}
                    onChange={async (value) => {
                        handleChangeEpisodesEntryValue(
                            value ? DateTime.from(value).format('YYYY-MM-DD') : null,
                            'startDate'
                        );
                    }}
                    value={episodesEntry.startDate ? new Date(episodesEntry.startDate) : null}
                />
            </FormGroup>
            <FormGroup
                label={translate.common.endDate}
                layout="vertical"
            >
                <DatePicker
                    error={getValidationErrorTranslation(
                        errors?.get('episodesAndJournals.episodes.' + errorKey + '.endDate')
                    )}
                    onChange={async (value) => {
                        handleChangeEpisodesEntryValue(
                            value ? DateTime.from(value).format('YYYY-MM-DD') : null,
                            'endDate'
                        );
                    }}
                    value={episodesEntry.endDate ? new Date(episodesEntry.endDate) : null}
                />
            </FormGroup>
            <FormGroup
                label={' '}
                layout="vertical"
            >
                <IconButton
                    icon="delete"
                    onClick={() => onDelete(episodesEntryId, episodesEntry.id)}
                />
            </FormGroup>
        </Box>
    );

    function handleChangeEpisodesEntryValue(value: string | Date | null, key: string) {
        const obj = { ...episodesEntry.toObject(), [key]: value };
        const newEpisodesEntry = new EpisodeRecord(obj);

        if (!validationKeys.includes('episodesAndJournals.episodes.' + errorKey + '.' + key)) {
            validationKeys.push('episodesAndJournals.episodes.' + errorKey + '.' + key);
        }

        onChange(newEpisodesEntry, episodesEntryId, validationKeys);
    }
}
